import { SagaIterator } from 'redux-saga';
import { PapiDeviation } from '@wix/da-papi-types';
import {
  changeOriginalStatus,
  changeOriginalStatusSuccess,
  declineOriginalOrder,
  fetchOriginalsOrderDetails,
  acceptOriginalOrder,
  toggleOriginalStatus,
} from '../../actions/studio/originals';
import { isApiError, requestPuppy } from '@wix/da-http-client';
import { call, all, put, select, takeLatest } from 'redux-saga/effects';
import { normalize } from '@wix/da-shared-react/pkg/redux/normalizr';
import { putEntities } from '@wix/da-shared-react/pkg/redux/entities/actions';
import { deviationSchema } from '@wix/da-shared-react/pkg/redux/normalizr/schemas/deviation';
import { OriginalStatusType } from '../../types/originals';
import { getIsOriginalOpen } from '../../selectors/studio/originals';

export function* changeOriginalStatusSaga(
  action: ReturnType<typeof changeOriginalStatus>
): SagaIterator {
  const { deviation, status } = action.payload;
  const isOpen = status === OriginalStatusType.OPEN;
  const response = yield call(
    requestPuppy,
    {
      method: 'post',
      url: '/originals/change_status',
      data: {
        deviationid: deviation.deviationId,
        is_open: isOpen,
      },
    },
    undefined,
    'dashared'
  );

  if (response && response.success) {
    const updatedDeviation = { ...deviation };
    // Update the deviation with new isOpen status
    if (!updatedDeviation.originals) {
      updatedDeviation.originals = {} as any;
    }
    (updatedDeviation.originals as any).isOpen = isOpen;
    const { entities } = normalize(updatedDeviation, deviationSchema);
    yield put(putEntities({ entities }));
    yield put(changeOriginalStatusSuccess(updatedDeviation));
  }
}

export function* toggleOriginalStatusSaga(
  action: ReturnType<typeof toggleOriginalStatus>
): SagaIterator {
  const deviation = action.payload as PapiDeviation;
  const isOpen = yield select(getIsOriginalOpen, deviation.deviationId);
  const status = isOpen ? OriginalStatusType.CLOSED : OriginalStatusType.OPEN;
  yield put(changeOriginalStatus({ deviation, status }));
}

export function* fetchOriginalsOrderDetailsSaga(
  action: ReturnType<typeof fetchOriginalsOrderDetails>
): SagaIterator {
  const { deviationId, responseId, onSuccess, onError } = action.payload;
  const response = yield call(
    requestPuppy,
    {
      method: 'get',
      url: `/originals/order`,
      params: {
        originals_deviationid: deviationId,
        responseid: responseId,
      },
    },
    undefined,
    'dashared'
  );
  if (response && !isApiError(response)) {
    yield call(onSuccess, response);
  } else {
    yield call(onError, response);
  }
}

export function* declineOriginalOrderSaga(
  action: ReturnType<typeof declineOriginalOrder>
): SagaIterator {
  const { deviationId, responseId, reason, onSuccess, onError } =
    action.payload;
  const response = yield call(
    requestPuppy,
    {
      method: 'post',
      url: `/originals/order/reject`,
      data: {
        originals_deviationid: deviationId,
        responseid: responseId,
        reason,
      },
    },
    undefined,
    'dashared'
  );

  if (response && !isApiError(response)) {
    yield call(onSuccess);
  } else {
    yield call(onError, response);
  }
}

export function* acceptOriginalOrderSaga(
  action: ReturnType<typeof acceptOriginalOrder>
): SagaIterator {
  const { deviationId, responseId, onSuccess, onError } = action.payload;
  const response = yield call(
    requestPuppy,
    {
      method: 'post',
      url: `/originals/order/accept`,
      data: {
        originals_deviationid: deviationId,
        responseid: responseId,
      },
    },
    undefined,
    'dashared'
  );
  if (response && !isApiError(response)) {
    yield call(onSuccess);
  } else {
    yield call(onError, response);
  }
}

export default function* originalsSaga(): any {
  yield all([
    takeLatest(changeOriginalStatus, changeOriginalStatusSaga),
    takeLatest(toggleOriginalStatus, toggleOriginalStatusSaga),
    takeLatest(fetchOriginalsOrderDetails, fetchOriginalsOrderDetailsSaga),
    takeLatest(declineOriginalOrder, declineOriginalOrderSaga),
    takeLatest(acceptOriginalOrder, acceptOriginalOrderSaga),
  ]);
}
